import React from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ChevronLeft, Phone, MessageSquare, MoreHorizontal } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PatientResponse } from "@/lib/types"
import { ageFromDob, formatDateOnly } from "@/lib/datetime"

interface PatientHeaderProps {
  patient: PatientResponse
  onCall?: () => void
  onSMS?: () => void
  onEditPatient?: () => void
  onUpdateConsent?: () => void
  onMarkInactive?: () => void
  onMarkDoNotCall?: () => void
  consentStatus?: string | null
  consentDate?: string | null
}

export function PatientHeader({
  patient,
  onCall,
  onSMS,
  onEditPatient,
  onUpdateConsent,
  onMarkInactive,
  onMarkDoNotCall,
  consentStatus,
  consentDate,
}: PatientHeaderProps) {
  const navigate = useNavigate()

  // A calendar date: formatted without any time-zone shift.
  const formatDob = formatDateOnly(patient.date_of_birth, "MM/dd/yyyy", patient.date_of_birth ?? "")

  const age = ageFromDob(patient.date_of_birth)

  const gender = patient.gender
    ? patient.gender.charAt(0).toUpperCase()
    : null

  const ageGender = [age, gender].filter(Boolean).join("")

  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return null
    // Simple US phone formatting
    const cleaned = phone.replace(/\D/g, "")
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
  }

  return (
    <div className="md:sticky md:top-0 z-10 bg-background/95 backdrop-blur-sm border-b shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
      <div className="px-4 py-2.5 md:px-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {/* Back button */}
          <button
            onClick={() => navigate("/patients")}
            className="flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Patients</span>
          </button>

          <div className="h-5 w-px bg-border shrink-0" />

          {/* Patient name + details */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 flex-1">
            <h1 className="text-base font-semibold md:whitespace-nowrap">
              {patient.first_name} {patient.last_name}
              {ageGender && (
                <span className="text-muted-foreground font-normal ml-1.5 text-sm">
                  ({ageGender})
                </span>
              )}
            </h1>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              {patient.date_of_birth && (
                <span>DOB: {formatDob}</span>
              )}
              {patient.id && (
                <>
                  <span className="text-muted-foreground/40">|</span>
                  <span>MRN: {patient.id.substring(0, 8)}</span>
                </>
              )}
              {patient.phone_number && (
                <>
                  <span className="text-muted-foreground/40">|</span>
                  <span>{formatPhone(patient.phone_number)}</span>
                </>
              )}
            </div>

            {/* Consent badge */}
            <div className="flex flex-wrap items-center gap-1.5 md:ml-1">
              {consentStatus === "active" && (
                <Badge className="bg-success/10 text-success hover:bg-success/10 text-[11px] px-2 py-0">
                  Consented
                </Badge>
              )}
              {consentStatus && consentStatus !== "active" && (
                <Badge variant="outline" className="border-warning/30 text-warning text-[11px] px-2 py-0">
                  {consentStatus}
                </Badge>
              )}
              {!consentStatus && (
                <Badge variant="outline" className="border-destructive/30 text-destructive text-[11px] px-2 py-0">
                  No Consent
                </Badge>
              )}
              {patient.status === "inactive" && (
                <Badge variant="destructive" className="text-[11px] px-2 py-0">Inactive</Badge>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            {patient.phone_number && (
              <>
                <Button variant="outline" size="sm" onClick={onCall} className="h-7 px-2.5 text-xs gap-1.5">
                  <Phone className="h-3 w-3" />
                  Call
                </Button>
                <Button variant="outline" size="sm" onClick={onSMS} className="h-7 px-2.5 text-xs gap-1.5">
                  <MessageSquare className="h-3 w-3" />
                  SMS
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={onEditPatient}>Edit Patient Info</DropdownMenuItem>
                <DropdownMenuItem onClick={onUpdateConsent}>Update Consent Status</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onMarkInactive}>Mark as Inactive</DropdownMenuItem>
                <DropdownMenuItem onClick={onMarkDoNotCall} className="text-destructive">Mark as Do Not Call</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientHeader
